import { createColumnHelper } from '@tanstack/react-table';
import {
  MessageSquare,
  Mail,
  Link,
  Gamepad2,
  AlertCircle,
  Zap,
  Users,
  Bell,
  TestTube,
  Edit,
} from 'lucide-react';

import { Button, LoadingButton } from '@@/buttons';
import { Badge, BadgeType } from '@@/Badge';
import { buildExpandColumn } from '@@/datatables/expand-column';
import { Icon, IconType } from '@@/Icon';

import {
  AlertSettingsRow,
  NotificationChannelType,
  NotificationChannel,
} from './types';

export const combinedColumnHelper = createColumnHelper<AlertSettingsRow>();

export function createCombinedColumns(
  onEditClick: (alertSettings: AlertSettingsRow) => void,
  onTestClick: (alertSettings: AlertSettingsRow) => void,
  isTestingConnectivity?: boolean
) {
  return [
    buildExpandColumn<AlertSettingsRow>(),
    combinedColumnHelper.accessor('name', {
      header: 'Name',
      cell: ({ getValue, row }) => {
        const isInternal = row.original.id === '1' && row.original.isInternal;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{getValue()}</span>
            {isInternal && (
              <Badge type="info" className="text-xs">
                System
              </Badge>
            )}
          </div>
        );
      },
    }),
    combinedColumnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        const variants: Record<string, BadgeType> = {
          disabled: 'muted',
          connected: 'success',
          disconnected: 'warn',
          error: 'danger',
        };
        return <Badge type={variants[status]}>{status}</Badge>;
      },
    }),
    combinedColumnHelper.accessor('enabled', {
      header: 'Enabled',
      cell: ({ getValue }) => (
        <Badge type={getValue() ? 'success' : 'muted'}>
          {getValue() ? 'Yes' : 'No'}
        </Badge>
      ),
    }),
    combinedColumnHelper.accessor('notificationChannels', {
      header: 'Channels',
      cell: ({ getValue }) => {
        const channels = getValue() || [];
        return <span className="text-gray-600 text-sm">{channels.length}</span>;
      },
    }),
    combinedColumnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <LoadingButton
            disabled={isTestingConnectivity || !row.original.enabled}
            loadingText="Testing..."
            isLoading={!!isTestingConnectivity}
            size="small"
            color="light"
            icon={TestTube}
            onClick={() => onTestClick(row.original)}
            data-cy={`test-endpoint-${row.original.id}`}
          >
            Test
          </LoadingButton>
          <Button
            size="small"
            color="light"
            icon={Edit}
            onClick={() => onEditClick(row.original)}
            data-cy={`edit-endpoint-${row.original.id}`}
          >
            Edit
          </Button>
        </div>
      ),
    }),
  ];
}

// Keep the original for backward compatibility
export const combinedColumns = createCombinedColumns(
  () => {},
  () => {},
  false
);

function getChannelIcon(type: NotificationChannelType): React.ReactElement {
  const iconMap: Record<NotificationChannelType, IconType> = {
    slack: MessageSquare,
    email: Mail,
    webhook: Link,
    discord: Gamepad2,
    pagerduty: AlertCircle,
    opsgenie: Zap,
    msteamsv2: Users,
  };
  const IconComponent = iconMap[type] || Bell;
  return <Icon icon={IconComponent} className="text-blue-500 h-4 w-4" />;
}

function getBadgeTypeForChannel(type: NotificationChannelType): BadgeType {
  const typeMap: Record<NotificationChannelType, BadgeType> = {
    slack: 'accent',
    email: 'success',
    webhook: 'warn',
    discord: 'info',
    pagerduty: 'warn',
    opsgenie: 'warn',
    msteamsv2: 'info',
  };
  return typeMap[type] || 'muted';
}

// Notification channels columns with configuration-specific information
export const notificationChannelColumns = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({
      getValue,
      row,
    }: {
      getValue: () => string;
      row: { original: NotificationChannel };
    }) => {
      const config = row.original.config || {};
      return (
        <div className="flex items-center gap-2">
          {getChannelIcon(row.original.type)}
          <span className="font-medium">
            {typeof config.name === 'string' ? config.name : getValue()}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ getValue }: { getValue: () => NotificationChannelType }) => {
      const type = getValue();
      const badgeType = getBadgeTypeForChannel(type);
      return <Badge type={badgeType}>{type}</Badge>;
    },
  },
  {
    accessorKey: 'config',
    header: 'Configuration',
    cell: ({
      getValue,
      row,
    }: {
      getValue: () => Record<string, unknown>;
      row: { original: NotificationChannel };
    }) => {
      const config = getValue();
      const { type } = row.original;

      function renderConfigDetails() {
        switch (type) {
          case 'slack':
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">URL:</span>
                  <span
                    className="max-w-48 truncate font-mono text-xs"
                    title={(config.api_url as string) || 'Not set'}
                  >
                    {((config.api_url as string) || 'Not set').length > 50
                      ? `${((config.api_url as string) || 'Not set').substring(
                          0,
                          50
                        )}...`
                      : (config.api_url as string) || 'Not set'}
                  </span>
                </div>
              </div>
            );

          case 'webhook':
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">URL:</span>
                  <span
                    className="max-w-48 truncate font-mono text-xs"
                    title={(config.url as string) || 'Not set'}
                  >
                    {((config.url as string) || 'Not set').length > 50
                      ? `${((config.url as string) || 'Not set').substring(
                          0,
                          50
                        )}...`
                      : (config.url as string) || 'Not set'}
                  </span>
                </div>
              </div>
            );

          case 'msteamsv2':
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">URL:</span>
                  <span
                    className="max-w-48 truncate font-mono text-xs"
                    title={(config.webhook_url as string) || 'Not set'}
                  >
                    {((config.webhook_url as string) || 'Not set').length > 50
                      ? `${(
                          (config.webhook_url as string) || 'Not set'
                        ).substring(0, 50)}...`
                      : (config.webhook_url as string) || 'Not set'}
                  </span>
                </div>
              </div>
            );

          case 'email':
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">SMTP Server:</span>
                  <span className="font-mono text-xs">
                    {(config.smtp_server as string) || 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">From:</span>
                  <span className="font-mono text-xs">
                    {(config.email_from as string) || 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">To:</span>
                  <span className="font-mono text-xs">
                    {(config.email_to as string) || 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">TLS:</span>
                  <span className="text-xs">
                    {config.require_tls ? 'Required' : 'Not required'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Auth:</span>
                  <span className="text-xs">
                    {config.auth_username ? 'Configured' : 'Not configured'}
                  </span>
                </div>
              </div>
            );

          case 'discord':
          case 'pagerduty':
          case 'opsgenie':
            return (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Name:</span>
                  <span className="text-xs font-medium">
                    {(config.name as string) || 'Not set'}
                  </span>
                </div>
                <div className="text-gray-500 text-xs italic">
                  Configuration details for {type} not yet implemented
                </div>
              </div>
            );

          default:
            return (
              <div className="text-gray-500 text-xs italic">
                Unknown channel type: {type}
              </div>
            );
        }
      }

      return <div className="text-sm">{renderConfigDetails()}</div>;
    },
  },
];
